// The pictures on a message in a Prime thread, as the session stores them: a
// blob id each (fetched from /api/blobs/{id} behind our token and shown from a
// blob URL, since an <img> cannot send the token), or base64 inline from
// before the blob store, drawn straight away. Blob pictures need the network
// the first time; offline the thread says they will show once it is back. A
// picture with neither (an older build stripped its reference) is named as gone.
import { api } from '@/runtime'
import { MessagePictures, type MessagePicture } from '@/components/MessagePictures'
import { useBlobUrls } from '@/hooks/useBlobUrls'
import { createBlobUrls } from '@/lib/blobUrls'
import { BLOB_ID, pictureSource, type SessionPicture } from '@/lib/blobs'

// One URL per blob, shared by every thread on the page; past MAX_CACHED the
// ones nothing shows are let go (lib/blobUrls.ts).
const MAX_CACHED = 32
const urls = createBlobUrls(MAX_CACHED)
const load = (id: string) => api.blob(id)

export const SessionPictures = ({ images }: { images: SessionPicture[] }) => {
  const ids = images.map((p) => p.blob ?? '').filter((id) => BLOB_ID.test(id))
  const { urls: got, online, failed, broken } = useBlobUrls(urls, ids, load)
  const shown: MessagePicture[] = []
  let gone = 0
  for (const p of images) {
    if (!p.blob && !p.data) {
      gone++
      continue
    }
    const src = pictureSource(p, got)
    if (src) shown.push(p.blob ? { url: src, onError: () => broken(p.blob!) } : { url: src })
  }
  const waiting = images.length - gone - shown.length
  return (
    <>
      {shown.length > 0 && <MessagePictures images={shown} />}
      {failed && waiting > 0 && (
        <p className="text-xs text-faint mt-1 text-right" data-testid="session-pictures-missing">
          {!online
            ? `${waiting === 1 ? 'A picture' : 'Pictures'} will show when you are online.`
            : `${waiting === 1 ? 'A picture' : 'Some pictures'} could not be loaded.`}
        </p>
      )}
      {gone > 0 && (
        <p className="text-xs text-faint mt-1 text-right" data-testid="session-pictures-gone">
          {gone === 1 ? 'A picture is no longer available.' : 'Some pictures are no longer available.'}
        </p>
      )}
    </>
  )
}
